import { useState, useEffect, useCallback, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useKeycloak } from "../utils/KeycloakProvider";

const useSocket = (namespace: string) => {
  const url = process.env.REACT_APP_SOCKET_URL || "";
  const keycloak = useKeycloak();
  const [socket, setSocket] = useState<Socket | null>(null);
  const listenersRef = useRef(new Map<string, Set<(data: any) => void>>());

  useEffect(() => {
    const newSocket = io(`${url}/${namespace}`, { transports: ["websocket"] });

    const authorize = async () => {
      await keycloak.updateToken(5);
      newSocket.emit("authorize", keycloak.token, ({ eventType }: { eventType: "authorized" | "unauthorized" }) => {
        if (eventType === "unauthorized") {
          console.warn("Unauthorized to connect to WebSocket");
          newSocket.disconnect();
        }
      });
    };

    newSocket.on("connect", () => {
      console.log(`Connected to WebSocket: ${namespace}`);
      authorize();
    });

    newSocket.on("disconnect", () => {
      console.warn(`Disconnected from WebSocket: ${namespace}`);
    });

    newSocket.onAny((event, data) => {
      // console.log(`Received event: ${event}`, data);
      listenersRef.current.get(event)?.forEach((callback) => callback(data));
    });

    setSocket(newSocket);

    return () => {
      newSocket.offAny();
      newSocket.disconnect();
    };
  }, [namespace, keycloak]); 

  const on = useCallback((event: string, callback: (data: any) => void) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }

    const eventListeners = listenersRef.current.get(event)!;
    if (!eventListeners.has(callback)) {
      eventListeners.add(callback);
      socket?.on(event, callback);
    }

    return () => {
      eventListeners.delete(callback);
      socket?.off(event, callback);
    };
  }, [socket]);

  const emit = useCallback((event: string, data: any) => {
    if (socket) {
      socket.emit(event, data);
    } else {
      console.warn(`Cannot emit event "${event}" - socket not connected`);
    }
  }, [socket]);

  return { socket, on, emit };
};

export default useSocket;
